import type { DashboardLayoutAccountScope } from "../types/dashboardLayoutAccountScope";

/** Broker account scope DTO — same shape as dashboard/workspace account scope. */
export type BrokerAccountRef = DashboardLayoutAccountScope;

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

export function brokerAccountRefKey(ref: BrokerAccountRef): string {
  return `${ref.brokerName.trim().toLowerCase()}:${ref.brokerAccountCode.trim()}`;
}

export function normalizeBrokerAccountRef(raw: unknown): BrokerAccountRef | null {
  if (!isRecord(raw)) return null;
  const brokerName = String(raw.brokerName ?? raw.broker_name ?? "")
    .trim()
    .toLowerCase();
  const brokerAccountCode = String(
    raw.brokerAccountCode ?? raw.broker_account_code ?? ""
  ).trim();
  if (!brokerName || !brokerAccountCode) return null;
  return { brokerName, brokerAccountCode };
}

export function normalizeBrokerAccountRefList(raw: unknown): BrokerAccountRef[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: BrokerAccountRef[] = [];
  for (const item of raw) {
    const ref = normalizeBrokerAccountRef(item);
    if (!ref) continue;
    const key = brokerAccountRefKey(ref);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ref);
  }
  return out;
}

/** Prefer `accounts[]`; fall back to singular broker fields. */
export function resolveEntityBrokerAccounts(input: {
  accounts?: unknown;
  brokerName?: string | null;
  brokerAccountCode?: string | null;
  broker_name?: string | null;
  broker_account_code?: string | null;
}): BrokerAccountRef[] {
  const fromList = normalizeBrokerAccountRefList(input.accounts);
  if (fromList.length > 0) return fromList;
  const brokerName = String(input.brokerName ?? input.broker_name ?? "")
    .trim()
    .toLowerCase();
  const brokerAccountCode = String(
    input.brokerAccountCode ?? input.broker_account_code ?? ""
  ).trim();
  if (!brokerName || !brokerAccountCode) return [];
  return [{ brokerName, brokerAccountCode }];
}

export function formatBrokerAccountRefLabel(ref: BrokerAccountRef): string {
  return ref.brokerAccountCode.trim() || ref.brokerName;
}
